import { Fragment, useEffect, useRef, useState, type CSSProperties } from "react";
import { Icon } from "@/components/common/Icon";
import { IconButton } from "@/components/common/IconButton";
import { Pill } from "@/components/infra/InfraParts";
import {
	captionStyle,
	cellStyle,
	pageTitleStyle,
	sectionHeadingStyle,
} from "@/components/infra/infra-styles";
import { PerformanceTrendChart } from "@/components/infra/PerformanceTrendChart";
import {
	getWafRequests,
	getWafSummary,
	type WafRequest,
	type WafRuleSeries,
	type WafSummary,
	type WafTopRow,
} from "@/lib/api";
import { formatDateTime, formatTime } from "@/lib/format-datetime";
import { WAF_PERIODS, WAF_TOP_ROWS, wafHitModeStatus, wafRuleLabel } from "@/lib/infra-waf";

const headerCellStyle: CSSProperties = { ...cellStyle, fontWeight: 500 };
const numberCellStyle: CSSProperties = captionStyle({
	...cellStyle,
	textAlign: "right",
	whiteSpace: "nowrap",
	fontVariantNumeric: "tabular-nums",
});
const monoStyle: CSSProperties = {
	fontFamily: "var(--font-mono)",
	fontSize: "var(--text-caption)",
	lineHeight: "var(--leading-normal)",
	color: "var(--fg-1)",
};
const panelStyle: CSSProperties = {
	padding: 8,
	borderRadius: "var(--radius-3)",
	background: "var(--surface-2)",
};

function formatCount(value: number): string {
	return Math.round(value).toLocaleString();
}

/**
 * 管理ページの「WAF」タブ (/admin/waf、SuperAdmin のみ)。WAF のルールに当たったリクエストの
 * ルールごとの推移と、ルール × パス × IP の上位を見せる。行を開くと見本のリクエストを読める。
 */
export function WafPage() {
	const [hours, setHours] = useState<number>(24);
	const [reloadKey, setReloadKey] = useState(0);
	const { data, error, loading } = useLoaded(() => getWafSummary(hours), `${hours}/${reloadKey}`);

	return (
		<div
			data-testid="waf-page"
			style={{ padding: 32, height: "100%", overflowY: "auto", background: "var(--surface-1)" }}
		>
			<h2 style={pageTitleStyle}>WAF</h2>
			<p style={captionStyle({ margin: "6px 0 16px" })}>
				入口の WAF のルールに当たったリクエストです (当たらなかったリクエストは記録していません)。
				「カウント」のルールはいまは数えるだけで、ブロックに替えると止まります。
				表の行を押すと見本のリクエストを表示します。
				{data !== null && ` 最終更新: ${formatTime(data.to)}`}
			</p>

			<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
				<select
					className="ds-select"
					data-testid="waf-period"
					value={hours}
					onChange={(e) => setHours(Number(e.target.value))}
					style={{ width: 160, height: 32 }}
				>
					{WAF_PERIODS.map((p) => (
						<option key={p.hours} value={p.hours}>
							{p.label}
						</option>
					))}
				</select>
				<IconButton
					icon="rotate"
					title="WAF を更新"
					data-testid="waf-refresh"
					size={28}
					disabled={loading}
					onClick={() => setReloadKey((k) => k + 1)}
					style={{ marginLeft: "auto" }}
				/>
			</div>

			{error !== null && (
				<p
					data-testid="waf-error"
					style={{ margin: "0 0 16px", fontSize: "var(--text-ui)", color: "var(--danger)" }}
				>
					{error}
				</p>
			)}
			{error === null && data === null && <p style={captionStyle({ margin: 0 })}>読み込み中…</p>}
			{data !== null && (
				<>
					{data.rules.length > 0 && <RuleTrends summary={data} hours={hours} />}
					{data.top.length === 0 ? (
						<p style={captionStyle({ margin: 0 })}>
							期間内にルールに当たったリクエストはありません。
						</p>
					) : (
						<TopTable rows={data.top} rules={data.rules} truncated={data.truncated} hours={hours} />
					)}
				</>
			)}
		</div>
	);
}

function InfoIcon({ text, testId }: { text: string; testId: string }) {
	return (
		<span
			data-testid={testId}
			title={text}
			data-tooltip-delay={0}
			style={{ display: "inline-flex", color: "var(--fg-2)", cursor: "help" }}
		>
			<Icon name="info" size={14} />
		</span>
	);
}

function RuleTrends({ summary, hours }: { summary: WafSummary; hours: number }) {
	return (
		<section data-testid="waf-trends" style={{ marginBottom: 32 }}>
			<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
				<h3 style={{ ...sectionHeadingStyle, margin: 0 }}>ルールごとの推移</h3>
				<InfoIcon
					testId="waf-trends-info"
					text={`Web ACL のすべてのルールの、${hours > 24 * 7 ? "1 日" : "1 時間"}ごとの件数です。マネージドルールは中のルールの当たりをまとめて数えます (内訳は下の表)。0 の区間は、そのルールに何も当たらなかった (数えても止めてもいない) ことを示します。`}
				/>
			</div>
			<div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
				{summary.rules.map((rule) => (
					<PerformanceTrendChart
						key={rule.rule}
						title={`${wafRuleLabel(rule.rule, null)} (${formatCount(rule.total)} 件${
							rule.blocked > 0 ? `、ブロック ${formatCount(rule.blocked)} 件` : ""
						})`}
						points={summary.buckets}
						series={[
							{ label: "件数", color: "var(--accent)", value: (b) => b.counts[rule.rule] ?? 0 },
							{
								label: "ブロック",
								color: "var(--danger)",
								value: (b) => b.blocked[rule.rule] ?? 0,
							},
						]}
						format={formatCount}
						testId={`waf-trend-${rule.rule}`}
					/>
				))}
			</div>
		</section>
	);
}

function rowKey(row: WafTopRow): string {
	return [
		row.rule,
		row.subRule,
		row.mode,
		row.pathPrefix,
		row.clientIp,
		row.country,
		row.authenticated,
	].join("|");
}

/** 行に出てくる値 (国・IP) と件数の合計を、件数の多い順に返す。値の分からない行は数えない。 */
function valueCounts(
	rows: WafTopRow[],
	valueOf: (row: WafTopRow) => string | null,
): { value: string; count: number }[] {
	const counts = new Map<string, number>();
	for (const row of rows) {
		const value = valueOf(row);
		if (value === null) continue;
		counts.set(value, (counts.get(value) ?? 0) + row.count);
	}
	return [...counts]
		.map(([value, count]) => ({ value, count }))
		.sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

function TopTable({
	rows,
	rules,
	truncated,
	hours,
}: {
	rows: WafTopRow[];
	rules: WafRuleSeries[];
	truncated: boolean;
	hours: number;
}) {
	const [openKey, setOpenKey] = useState<string | null>(null);
	const [selectedRule, setSelectedRule] = useState<string | null>(null);
	const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
	const [selectedIp, setSelectedIp] = useState<string | null>(null);
	// 期間・ルール・国を替えて選んだ値が選択肢から消えたら、すべてに戻す。
	const rule = rules.find((r) => r.rule === selectedRule) ?? null;
	const ruleRows = rule === null ? rows : rows.filter((r) => r.rule === rule.rule);
	const countries = valueCounts(ruleRows, (r) => r.country);
	const country = countries.find((c) => c.value === selectedCountry)?.value ?? null;
	const countryRows = country === null ? ruleRows : ruleRows.filter((r) => r.country === country);
	const ips = valueCounts(countryRows, (r) => r.clientIp);
	const ip = ips.find((i) => i.value === selectedIp)?.value ?? null;
	const shown = (ip === null ? countryRows : countryRows.filter((r) => r.clientIp === ip)).slice(
		0,
		WAF_TOP_ROWS,
	);
	return (
		<section data-testid="waf-top">
			<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
				<h3 style={{ ...sectionHeadingStyle, margin: 0 }}>当たった先の上位</h3>
				<InfoIcon
					testId="waf-top-info"
					text="ルール × パスの先頭 × IP ごとの件数です。1 件が複数のルールに当たると、それぞれの行に数えます。「認証」はログイン・ランナーなどの認証ヘッダーが付いていたかで、付いていれば自社の利用の誤検知の可能性が高いです。"
				/>
				<select
					className="ds-select"
					data-testid="waf-top-rule-filter"
					value={rule?.rule ?? ""}
					onChange={(e) => {
						setSelectedRule(e.target.value || null);
						setOpenKey(null);
					}}
					style={{ width: 280, height: 32, marginLeft: "auto" }}
				>
					<option value="">すべてのルール</option>
					{rules.map((rule) => (
						<option key={rule.rule} value={rule.rule}>
							{wafRuleLabel(rule.rule, null)} ({formatCount(rule.total)} 件)
						</option>
					))}
				</select>
				<select
					className="ds-select"
					data-testid="waf-top-country-filter"
					value={country ?? ""}
					onChange={(e) => {
						setSelectedCountry(e.target.value || null);
						setOpenKey(null);
					}}
					style={{ width: 160, height: 32 }}
				>
					<option value="">すべての国</option>
					{countries.map((c) => (
						<option key={c.value} value={c.value}>
							{c.value} ({formatCount(c.count)} 件)
						</option>
					))}
				</select>
				<select
					className="ds-select"
					data-testid="waf-top-ip-filter"
					value={ip ?? ""}
					onChange={(e) => {
						setSelectedIp(e.target.value || null);
						setOpenKey(null);
					}}
					style={{ width: 240, height: 32 }}
				>
					<option value="">すべての IP</option>
					{ips.map((i) => (
						<option key={i.value} value={i.value}>
							{i.value} ({formatCount(i.count)} 件)
						</option>
					))}
				</select>
			</div>
			{truncated && (
				<p
					data-testid="waf-top-truncated"
					style={captionStyle({ margin: "0 0 8px", color: "var(--warning)" })}
				>
					組み合わせが多く、集計の行数が上限 (1 万行)
					に達しました。件数の少ない組み合わせの一部は表と件数に入っていません。
				</p>
			)}
			<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
				<thead>
					<tr style={captionStyle({ textAlign: "left" })}>
						<th style={headerCellStyle}>ルール</th>
						<th style={{ ...headerCellStyle, width: 104 }}>扱い</th>
						<th style={{ ...headerCellStyle, width: "22%" }}>パス</th>
						<th style={{ ...headerCellStyle, width: 168 }}>IP</th>
						<th style={{ ...headerCellStyle, width: 56 }}>認証</th>
						<th style={{ ...headerCellStyle, width: 88, textAlign: "right" }}>件数</th>
					</tr>
				</thead>
				<tbody>
					{shown.map((row) => {
						const key = rowKey(row);
						const open = openKey === key;
						const status = wafHitModeStatus(row.mode);
						return (
							<Fragment key={key}>
								<tr data-testid={`waf-top-row-${key}`}>
									<td style={cellStyle}>
										<button
											type="button"
											data-testid={`waf-top-toggle-${key}`}
											onClick={() => setOpenKey(open ? null : key)}
											style={{
												display: "block",
												maxWidth: "100%",
												padding: 0,
												border: "none",
												background: "none",
												font: "inherit",
												fontSize: "var(--text-caption)",
												color: "var(--fg-1)",
												textAlign: "left",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
												cursor: "pointer",
											}}
										>
											{wafRuleLabel(row.rule, row.subRule)}
										</button>
									</td>
									<td style={cellStyle}>
										<span title={status.description}>
											<Pill label={status.label} tone={status.tone} />
										</span>
									</td>
									<td
										title={row.pathPrefix}
										data-tooltip-if-truncated
										style={{
											...captionStyle(cellStyle),
											fontFamily: "var(--font-mono)",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{row.pathPrefix}
									</td>
									<td style={captionStyle({ ...cellStyle, whiteSpace: "nowrap" })}>
										{row.clientIp}
										{row.country !== null && ` (${row.country})`}
									</td>
									<td style={captionStyle(cellStyle)}>{row.authenticated ? "あり" : "なし"}</td>
									<td style={numberCellStyle}>{formatCount(row.count)}</td>
								</tr>
								{open && (
									<tr>
										<td colSpan={6} style={{ ...cellStyle, borderTop: "none" }}>
											<WafRequests row={row} hours={hours} />
										</td>
									</tr>
								)}
							</Fragment>
						);
					})}
				</tbody>
			</table>
			{shown.length === 0 && (
				<p style={captionStyle({ margin: "8px 0 0" })}>
					{rule !== null && rule.total > 0
						? `このルールの当たり (${formatCount(rule.total)} 件) は、件数の多い組み合わせに押し出されて上位の集計に入りませんでした。`
						: "期間内にこのルールに当たったリクエストはありません。"}
				</p>
			)}
		</section>
	);
}

/** 表の 1 行に当たる見本のリクエスト。開くと WAF のログの全文が読める。 */
function WafRequests({ row, hours }: { row: WafTopRow; hours: number }) {
	const { data, error } = useLoaded(
		() => getWafRequests({ hours, row }),
		`${hours}/${rowKey(row)}`,
	);
	return (
		<div data-testid="waf-requests" style={panelStyle}>
			{error !== null && (
				<p style={{ margin: 0, fontSize: "var(--text-ui)", color: "var(--danger)" }}>{error}</p>
			)}
			{error === null && data === null && <p style={captionStyle({ margin: 0 })}>読み込み中…</p>}
			{data !== null &&
				(data.length === 0 ? (
					<p style={captionStyle({ margin: 0 })}>見本のリクエストが見つかりませんでした。</p>
				) : (
					<>
						<p style={captionStyle({ margin: "0 0 4px" })}>
							新しい順に最大 50 件。認証ヘッダーの値はログに残していません。
						</p>
						{data.map((request, i) => (
							<WafRequestItem key={`${request.timestamp}-${i}`} request={request} />
						))}
					</>
				))}
		</div>
	);
}

function WafRequestItem({ request }: { request: WafRequest }) {
	return (
		<details>
			<summary
				style={captionStyle({
					display: "flex",
					gap: 8,
					padding: "2px 0",
					color: "var(--fg-1)",
					cursor: "pointer",
				})}
			>
				<span style={{ whiteSpace: "nowrap", color: "var(--fg-3)" }}>
					{formatDateTime(request.timestamp)}
				</span>
				<span style={{ whiteSpace: "nowrap" }}>{request.method}</span>
				<span
					style={{
						minWidth: 0,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						fontFamily: "var(--font-mono)",
					}}
				>
					{request.uri}
					{request.args ? `?${request.args}` : ""}
				</span>
			</summary>
			<div className="select-text" style={{ ...monoStyle, padding: "4px 0 8px 16px" }}>
				<div>
					当たったルール:{" "}
					{request.hits
						.map((h) => `${wafRuleLabel(h.rule, h.subRule)} (${wafHitModeStatus(h.mode).label})`)
						.join("、")}
				</div>
				<div>User-Agent: {request.userAgent ?? "なし"}</div>
				<pre
					style={{
						...monoStyle,
						margin: "4px 0 0",
						whiteSpace: "pre-wrap",
						wordBreak: "break-all",
						color: "var(--fg-2)",
					}}
				>
					{request.log}
				</pre>
			</div>
		</details>
	);
}

function useLoaded<T>(load: () => Promise<T>, key: string) {
	const [data, setData] = useState<T | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const loadRef = useRef(load);
	loadRef.current = load;

	useEffect(() => {
		let current = true;
		setLoading(true);
		loadRef
			.current()
			.then((result) => {
				if (!current) return;
				setData(result);
				setError(null);
			})
			.catch((err: Error) => {
				if (current) setError(err.message);
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [key]);

	return { data, error, loading };
}
